import React from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../types';
import { ApiService } from '../utils/api';
import { ExternalLink, Tag } from 'lucide-react';

interface ProductCardProps {
  product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  return (
    <Link
      to={`/product/${product.id}`}
      className="group bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden card-hover"
    >
      {/* Product Image */}
      <div className="aspect-w-16 aspect-h-12 bg-gradient-to-br from-gray-100 to-gray-200">
        {product.image_url && !product.image_url.includes('placeholder') ? (
          <img 
            src={product.image_url} 
            alt={product.canonical_name}
            className="w-full h-48 object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
        ) : null}
        <div className={`w-full h-48 flex items-center justify-center ${product.image_url && !product.image_url.includes('placeholder') ? 'hidden' : ''}`}>
          <div className="text-center">
            <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Tag className="w-10 h-10 text-primary-600" />
            </div>
            <p className="text-gray-500 text-sm font-medium">{product.brand}</p>
          </div>
        </div>
      </div>
      
      {/* Product Info */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors line-clamp-2">
              {product.canonical_name}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {product.brand} • {ApiService.getCategoryDisplayName(product.category)}
            </p>
          </div>
        </div>
        
        {/* Price */}
        <div className="flex items-center justify-between">
          <div>
            {product.lowest_price ? (
              <div>
                <span className="text-2xl font-bold text-green-600">
                  {ApiService.formatPrice(product.lowest_price)}
                </span>
                <p className="text-sm text-gray-500">Best Price</p>
              </div>
            ) : (
              <div>
                <span className="text-lg font-medium text-gray-500">
                  Price on request
                </span>
                <p className="text-sm text-gray-400">Check vendors</p>
              </div>
            )}
          </div>
          
          <div className="flex items-center text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="text-sm font-medium mr-1">View Details</span>
            <ExternalLink className="w-4 h-4" />
          </div>
        </div>
        
        {/* Category Badge */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200">
            {ApiService.getCategoryDisplayName(product.category)}
          </span>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
